"use client"

import { Button, IconButton, Text } from "@/elements"
import { PageMaxWidth } from "@/components"
import { FragmentType, graphql, useFragment } from "@/gql"
import { UnauthenticatedJobPostingInfoFragmentDoc } from "@/gql/graphql"
import { CalendarPlus } from "@/icons/outline"
import { UserIcon } from "@/icons/solid"
import { InformationCircleIcon, Square2StackIcon } from "@heroicons/react/24/outline"
import * as Tooltip from "@radix-ui/react-tooltip"
import { formatDistanceToNow } from "date-fns"
import Link from "next/link"
import { FC, useCallback, useEffect } from "react"
import { JobTitleColorised } from "@/components/JobTitleColorised"
import { SkillPill } from "../components/SkillPill"
import { copyToClipboard } from "@/utils/copyToClipboard"
import { UnauthorizedNav } from "@/components/Nav/UnauthorizedNav"
import adjustDateForTimezone from "@/utils/dates"
import { decodeGraphqlNodeId } from "@/tracking"
import { ampli } from "@/ampli"

graphql(`
  fragment UnauthenticatedJobPostingInfo on JobPosting {
    id
    __typename
    ...JobTitleColorised
    skills {
      edges {
        node {
          id
          __typename
          name
        }
      }
    }
    payString
    employmentTypeDisplay
    publishedAt
  }
`)

interface Props {
  serverData: FragmentType<typeof UnauthenticatedJobPostingInfoFragmentDoc> | undefined | null
}

export const UnauthenticatedClientPage: FC<Props> = props => {
  const jobPost = useFragment(UnauthenticatedJobPostingInfoFragmentDoc, props.serverData)
  const now = adjustDateForTimezone(new Date().toISOString())!

  const viewJobPosting = useCallback(() => {
    if (jobPost?.id) {
      fetch("/api/view/job", {
        method: "POST",
        body: JSON.stringify({ id: jobPost?.id }),
      })
    }
  }, [jobPost?.id])

  useEffect(() => {
    viewJobPosting()
  }, [viewJobPosting, jobPost?.id])

  useEffect(() => {
    ampli.jobDetailView({
      job_id: decodeGraphqlNodeId(jobPost?.id || "").numericalId || "",
      entry_point: "job-page",
      redacted: true,
    })
  }, [jobPost?.id])

  return (
    <div className="max-w-screen relative flex justify-center overflow-hidden">
      <PageMaxWidth className="fixed top-0 bg-bg-1">
        <UnauthorizedNav />
      </PageMaxWidth>

      <div className="mx-auto mt-[72px] w-full overflow-y-scroll pb-[150px] md:max-w-[720px] md:px-12">
        <div className="h-[260px] w-full bg-bg-3 p-12" />
        <div className="px-5 sm:px-16 md:px-0">
          <div className="mt-6 flex flex-row justify-between sm:mt-9">
            <div className="flex flex-row items-center gap-x-3">
              <div className="flex size-12 items-center justify-center rounded-full bg-bg-3">
                <UserIcon className="size-[28px] fill-fg-2" />
              </div>
              <div className="flex flex-col justify-center gap-y-1">
                <div className="h-[14px] w-[126px] rounded-[2px] bg-bg-4" />
                <div className="h-[11px] w-[74px] rounded-[2px] bg-bg-4" />
              </div>
              <SignUpTooltip text="Sign up to see job poster's information" />
            </div>
            <div className="flex flex-row gap-x-3">
              <IconButton
                variant="white"
                Icon={Square2StackIcon}
                onClick={() => copyToClipboard(window.location.href)}
              />
            </div>
          </div>
          <div className="mt-6">
            <JobTitleColorised commonWordColor="text-fg-2" textVariant="h4" jobPost={jobPost} />
          </div>

          {jobPost?.skills?.edges && jobPost?.skills?.edges.length > 0 ? (
            <div className="mt-6 flex flex-row flex-wrap gap-1">
              {jobPost?.skills?.edges?.map(edge => {
                return <SkillPill key={edge?.node?.id} skill={edge?.node?.name} />
              })}
            </div>
          ) : null}

          <div className="mt-6">
            <div className="flex w-fit flex-row gap-x-2 rounded-full bg-bg-2 px-3 py-2">
              <CalendarPlus className="size-[18px] stroke-fg-2" />
              <Text variant="caption" className="text-fg-2">
                Posted{" "}
                {formatDistanceToNow(jobPost?.publishedAt || adjustDateForTimezone(new Date().toISOString())!, {
                  addSuffix: true,
                })}
              </Text>
            </div>
          </div>

          <div className="mt-6 h-[1px] w-full bg-bg-3"></div>
          <div className="mt-4 flex flex-row gap-x-2 sm:mt-6">
            <Text className="text-fg-2">Description</Text>
            <SignUpTooltip text="Sign up to see job information" />
          </div>
          <div className="mt-3 flex flex-col gap-y-4 md:mt-5">
            <div className="h-[11px] w-full rounded-[2px] bg-bg-4" />
            <div className="h-[11px] w-full rounded-[2px] bg-bg-4" />
            <div className="h-[11px] w-1/3 rounded-[2px] bg-bg-4" />
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 flex w-full justify-center sm:bottom-9">
        <div
          style={{
            boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)",
          }}
          className="flex w-full flex-row items-center justify-between border border-bg-3 bg-bg-1 px-[34px] py-[25px] sm:mx-6 sm:max-w-[768px] sm:rounded-xl">
          <div className="flex flex-col gap-y-1">
            <Text variant="h5">{jobPost?.payString}</Text>
            <Text variant="label" className="text-fg-2">
              <span className="hidden sm:inline">Type: </span>
              {jobPost?.employmentTypeDisplay}
            </Text>
          </div>
          <Link href={"/sign-up"}>
            <Button>Sign up to apply</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

const SignUpTooltip = ({ text }: { text: string }) => {
  return (
    <div>
      <Tooltip.Provider delayDuration={200}>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <InformationCircleIcon width={24} height={24} className="stroke-fg-1" />
          </Tooltip.Trigger>
          <Tooltip.Content sideOffset={2} side="top">
            <div
              className="max-w-[200px] rounded-md bg-bg-1 p-4 text-center"
              style={{
                boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.10)",
              }}>
              <Text variant="label">{text}</Text>
            </div>
            <Tooltip.Arrow className="fill-bg-1" />
          </Tooltip.Content>
        </Tooltip.Root>
      </Tooltip.Provider>
    </div>
  )
}
